/**
 * Configuração runtime do app.
 *
 * Resolve variáveis de ambiente em duas camadas:
 *   1. `window.__ENV__` — injetado em runtime pelo `docker-entrypoint.sh`
 *      (via envsubst) ao iniciar o container nginx. Permite trocar VITE_API_URL
 *      sem rebuild da imagem Docker.
 *   2. `import.meta.env` — resolvido em build-time pelo Vite. Usado em dev e
 *      como fallback quando window.__ENV__ não está definido ou ainda contém
 *      o placeholder literal "${VITE_API_URL}" (caso o envsubst não tenha
 *      rodado — ex: rodando `vite preview` localmente).
 *
 * Spec: §19 Build, Deploy e nginx → "Variáveis de ambiente em runtime".
 */

declare global {
  interface Window {
    __ENV__?: {
      VITE_API_URL?: string;
      VITE_APP_NAME?: string;
    };
  }
}

/** Retorna o valor de window.__ENV__[key] se for um valor real, ou undefined
 *  se estiver vazio ou ainda for o placeholder literal "${VAR}". */
function readRuntimeEnv(key: 'VITE_API_URL' | 'VITE_APP_NAME'): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const value = window.__ENV__?.[key];
  if (!value) return undefined;
  // Placeholder ainda não substituído pelo envsubst (ex: rodando local sem Docker)
  if (value.startsWith('${') && value.endsWith('}')) return undefined;
  return value;
}

export const config = {
  /** URL base da API do Orchestrator. Em dev usa proxy do Vite (string vazia → relativo). */
  apiUrl:
    readRuntimeEnv('VITE_API_URL') ??
    import.meta.env.VITE_API_URL ??
    '',

  /** Nome do app exibido na UI. */
  appName:
    readRuntimeEnv('VITE_APP_NAME') ??
    import.meta.env.VITE_APP_NAME ??
    'iTap URA',

  /** Idioma default (resolvido apenas em build-time — não exposto em runtime). */
  defaultLanguage: import.meta.env.VITE_DEFAULT_LANGUAGE ?? 'pt-BR',

  /** Modo de execução (dev/prod) — usado para logs condicionais. */
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;

export type AppConfig = typeof config;
